import React, { useEffect, useRef, useState, useCallback } from "react"
import {
  LinearProgress,
  Box,
  BoxProps,
  useTheme,
  LinearProgressProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"

export interface ICoolDownProgressProps extends BoxProps {
  duration?: number // cooldown duration in milliseconds (default 5000)
  reverse?: boolean // true = progress counts down (100 → 0), false = counts up (0 → 100)
  repeat?: boolean // if true, cooldown repeats infinitely
  linearProgressProps?: Partial<LinearProgressProps> // props for MUI LinearProgress
  onComplete?: () => void // callback after each cooldown cycle
}

export const CoolDownProgress: React.FC<ICoolDownProgressProps> = ({
  duration = 5000,
  reverse = false,
  repeat = false,
  sx = {},
  linearProgressProps = {},
  onComplete,
  ...props
}) => {
  const theme = useTheme()
  const { root, linearProgress } =
    theme.components?.["CoolDownProgress"]?.styleOverrides || {}

  const [progress, setProgress] = useState<number>(reverse ? 100 : 0)
  const startTimeRef = useRef<number>(Date.now())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback(() => {
    startTimeRef.current = Date.now()

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      const ratio = Math.min(1, elapsed / duration)

      const newProgress = reverse
        ? Math.max(0, 100 - ratio * 100)
        : Math.min(100, ratio * 100)

      setProgress(newProgress)

      if (elapsed >= duration) {
        clearInterval(intervalRef.current!)

        if (typeof onComplete === "function") onComplete()

        if (repeat) {
          startCooldown() // restart cycle
        }
      }
    }, 100)
  }, [duration, reverse, repeat, onComplete])

  useEffect(() => {
    startCooldown()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startCooldown])

  return (
    <Box sx={deepmerge(root, sx)} {...props}>
      <LinearProgress
        sx={linearProgress}
        variant="determinate"
        className="CoolDownProgress-linearProgress"
        value={progress}
        {...linearProgressProps}
      />
    </Box>
  )
}
